import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { CircularProgress, Box, Typography } from '@mui/material';
import { ROUTES } from '@/constants';

type Permission = keyof import('@contexts/AuthContext').IUser['permissions'];

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Uma permissão exige exatamente ela; um array exige QUALQUER UMA delas (OR) — usado por
   * páginas que unificam conteúdo antes espalhado em rotas com permissões diferentes (pedido do
   * usuário, 14/09: Painel Público passa a incluir Sugestões/Caixa de Sugestões) sem tirar acesso
   * de quem já tinha só uma das permissões envolvidas. */
  requiredPermission?: Permission | Permission[];
  /** Apenas usuários com isSystemAdmin (suporte Vover) */
  systemAdminOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredPermission,
  systemAdminOnly,
}) => {
  const { user, isLoading, hasPermission } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!user) {
    // Se estiver tentando acessar /app sem estar logado, redirecionar para login
    if (location.pathname.startsWith(ROUTES.APP)) {
      const redirect = encodeURIComponent(`${location.pathname}${location.search || ''}`);
      return <Navigate to={`${ROUTES.LOGIN}?redirect=${redirect}`} replace />;
    }
    return <Navigate to={ROUTES.HOME} replace />;
  }

  if (systemAdminOnly && user.isSystemAdmin !== true) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  const missingPermission = requiredPermission
    ? Array.isArray(requiredPermission)
      ? !requiredPermission.some((p) => hasPermission(p))
      : !hasPermission(requiredPermission)
    : false;

  if (missingPermission) {
    // Usuário autenticado, mas sem a permissão exigida para esta rota.
    // Mantém o layout do app (menu visível) e mostra apenas uma mensagem central.
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            maxWidth: 480,
            textAlign: 'center',
            p: 4,
            borderRadius: 3,
            boxShadow: 3,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h5" component="h1" gutterBottom>
            Acesso Negado
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Você não tem permissão para acessar esta página. Se precisar de acesso,
            entre em contato com o administrador do sistema.
          </Typography>
        </Box>
      </Box>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
